import ReusableTable from 'views/QuicklypayDashboards/components/Tables/NormalTable';
import styles from './ManageClients.module.css';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid } from '@mui/material';
import { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { createClientApi, listClientApi, updateClientApi } from 'utils/API/Superdistributor APIs/SuperdistributorApiCalls';
import { globalNotifySuccess } from 'views/utilities/AlertToast';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Skeleton } from '@mui/material';
import { Switch, FormControlLabel } from '@mui/material';

const validationSchema = Yup.object({
    name: Yup.string().required('Required'),
    phone: Yup.string()
        .matches(/^\d{10}$/, 'Enter a valid 10-digit mobile number')
        .required('Required'),
    qr_commission: Yup.number().min(0).max(100).required('Required'),
    card_commission: Yup.number().min(0).max(100).required('Required')
});
const ManageClients = () => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState({ add: false, list: false });
    const [clientList, setClientList] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [initialValues, setInitialValues] = useState({
        name: '',
        phone: '',
        qr_commission: '',
        card_commission: '',
        status: true
    });
    const [searchQuery, setSearchQuery] = useState('');

    //utility handlers>>>
    const formattedData = clientList.map((item) => {
        const istDate = new Date(item.created_at).toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true // Use false for 24-hour format
        });

        return {
            ...item,
            created_at: istDate
        };
    });
    const filteredData = formattedData.filter((item) => {
        const query = searchQuery.toLowerCase();
        return item.name.toLowerCase().includes(query) || item.phone.toString().includes(query);
    });
    const columns = [
        { label: 'Name', field: 'name' },
        { label: 'Mobile', field: 'phone' },
        { label: 'Qr Commission', field: 'qr_commission' },
        { label: 'Card Commission', field: 'card_commission' },
        { label: 'Created On', field: 'created_at' },
        {
            label: 'Status',
            render: (row) => (
                <span className={row.status === 1 ? styles.statusEnabled : styles.statusDisabled}>
                    {row.status === 1 ? 'Active' : 'Inactive'}
                </span>
            )
        },
        {
            label: 'Actions',
            render: (row) => (
                <>
                    <button onClick={() => handleEditClient(row)}>Edit</button>
                    {/* <button onClick={() => alert(`Deleting ${row.name}`)}>Delete</button> */}
                </>
            )
        }
    ];
    const TableSkeleton = () => (
        <div className={styles.tableWrapper}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        {columns.map((col, index) => (
                            <th key={index} className={styles.tableHeader}>
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {[...Array(5)].map((_, i) => (
                        <tr key={i} className={styles.tableRow}>
                            {columns.map((_, j) => (
                                <td key={j} className={styles.tableCell}>
                                    <Skeleton variant="text" width="80%" />
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
    //api calls>>>>
    const handleAddClient = async (values) => {
        setLoading((prev) => ({ ...prev, add: true }));

        if (editMode) {
            const response = await updateClientApi({ ...values, id: initialValues.id });
            if (response?.status === 200) {
                globalNotifySuccess('Client updated successfully.');
                handleListClients();
            }
        } else {
            const response = await createClientApi(values);
            if (response?.status === 200) {
                globalNotifySuccess('Client added successfully.');
                handleListClients();
            }
        }

        setLoading((prev) => ({ ...prev, add: false }));
        setOpen(false);
        setEditMode(false); // reset
        setInitialValues({ name: '', phone: '', qr_commission: '', card_commission: '' });
    };
    const handleListClients = async () => {
        setLoading((prev) => ({ ...prev, list: true }));
        const response = await listClientApi();
        if (response.status === 200) {
            const list = response?.data?.data;
            setClientList(list);
        }
        setLoading((prev) => ({ ...prev, list: false }));
    };
    const handleEditClient = (client) => {
        setInitialValues({
            name: client.name,
            phone: client.phone,
            qr_commission: client.qr_commission,
            card_commission: client.card_commission,
            id: client.id, // add id for update reference
            status: client.status === 1 ? true : false
        });
        setEditMode(true);
        setOpen(true);
    };
    useEffect(() => {
        handleListClients();
    }, []);
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div>
                <div className={styles.headerWrapper}>
                    <h2 className={styles.sectionHeading}>Clients List</h2>
                    <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="Search by name/mobile"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={styles.searchInput}
                        />
                        <button className={styles.addClientButton} onClick={() => setOpen(true)}>
                            + Add Client
                        </button>
                    </div>
                </div>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.4 }}>
                    {loading.list ? <TableSkeleton /> : <ReusableTable columns={columns} rows={filteredData} />}
                </motion.div>
                <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}>
                        {editMode ? 'Edit Client' : 'Add New Client'}
                    </DialogTitle>
                    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleAddClient}>
                        {({ values, handleChange, touched, errors }) => (
                            <Form autoComplete="off">
                                <DialogContent dividers>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12}>
                                            <TextField
                                                label="Name"
                                                fullWidth
                                                name="name"
                                                value={values.name}
                                                onChange={handleChange}
                                                error={touched.name && Boolean(errors.name)}
                                                helperText={touched.name && errors.name}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <TextField
                                                label="Mobile"
                                                fullWidth
                                                name="phone"
                                                autoComplete="new-mobile"
                                                value={values.phone}
                                                onChange={handleChange}
                                                error={touched.phone && Boolean(errors.phone)}
                                                helperText={touched.phone && errors.phone}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <TextField
                                                label="QR Commission %"
                                                fullWidth
                                                name="qr_commission"
                                                type="number"
                                                value={values.qr_commission}
                                                onChange={handleChange}
                                                error={touched.qr_commission && Boolean(errors.qr_commission)}
                                                helperText={touched.qr_commission && errors.qr_commission}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <TextField
                                                label="Card Commission %"
                                                fullWidth
                                                name="card_commission"
                                                type="number"
                                                value={values.card_commission}
                                                onChange={handleChange}
                                                error={touched.card_commission && Boolean(errors.card_commission)}
                                                helperText={touched.card_commission && errors.card_commission}
                                            />
                                        </Grid>
                                        {editMode && (
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={values.status}
                                                            onChange={handleChange}
                                                            name="status"
                                                            color="success"
                                                        />
                                                    }
                                                    label={values.status ? 'Active' : 'Inactive'}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </DialogContent>
                                <DialogActions sx={{ padding: '16px' }}>
                                    <Button
                                        onClick={() => {
                                            setOpen(false);
                                            setEditMode(false);
                                            setInitialValues({ name: '', phone: '', qr_commission: '', card_commission: '' });
                                        }}
                                        color="inherit"
                                    >
                                        Cancel
                                    </Button>
                                    <LoadingButton
                                        loading={loading.add}
                                        type="submit"
                                        variant="contained"
                                        className={styles.addClientButton}
                                    >
                                        {editMode ? 'Update' : 'Submit'}
                                    </LoadingButton>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </Dialog>
            </div>
        </motion.div>
    );
};

export default ManageClients;
